import { useEffect, useState } from "react";

/**
 * Returns `true` when the document is currently hidden (e.g. the user has
 * switched to a different browser tab or minimized the window).
 *
 * Marketing motion that auto-advances on a timer should gate its
 * `setInterval`/`requestAnimationFrame` loop on this so we don't burn
 * CPU/GPU re-rendering content the visitor cannot see. When the tab
 * becomes visible again, returning `false` lets the caller's effect
 * resume from the current state without losing position.
 */
export function useVisibilityPause(): boolean {
  const [hidden, setHidden] = useState<boolean>(() => {
    if (typeof document === "undefined") return false;
    return document.visibilityState === "hidden";
  });

  useEffect(() => {
    if (typeof document === "undefined") return;
    const onChange = () => {
      setHidden(document.visibilityState === "hidden");
    };
    // Sync once on mount in case the initial render was SSR or the
    // visibility flipped between hydration and the effect firing.
    onChange();
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);

  return hidden;
}
